import React, { useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useTheme } from "@/context/ThemeContext";
import {
  LayoutDashboard, Car, Gavel, Wrench, Receipt, Users, FileText,
  BarChart3, Shield, Moon, Sun, LogOut, Search, Bell, UserCog, Menu, X,
  Sparkles, Upload, ClipboardCheck,
} from "lucide-react";
import { Toaster } from "@/components/ui/sonner";

const NAV_ALL = [
  { to: "/", icon: LayoutDashboard, label: "Dashboard", testid: "nav-dashboard", roles: ["admin", "employee", "accountant"], end: true },
  { to: "/inventory", icon: Car, label: "Inventory", testid: "nav-inventory", roles: ["admin", "employee", "accountant"] },
  { to: "/auctions", icon: Gavel, label: "Auctions", testid: "nav-auctions", roles: ["admin", "employee", "accountant"] },
  { to: "/repairs", icon: Wrench, label: "Repairs", testid: "nav-repairs", roles: ["admin", "employee", "accountant"] },
  { to: "/expenses", icon: Receipt, label: "Expenses", testid: "nav-expenses", roles: ["admin", "accountant"] },
  { to: "/customers", icon: Users, label: "Customers", testid: "nav-customers", roles: ["admin", "employee", "accountant"] },
  { to: "/sales", icon: FileText, label: "Sales", testid: "nav-sales", roles: ["admin", "employee", "accountant"] },
  { to: "/business-summary", icon: ClipboardCheck, label: "Summary", testid: "nav-summary", roles: ["admin", "employee", "accountant"] },
  { to: "/insights", icon: Sparkles, label: "Insights", testid: "nav-insights", roles: ["admin", "accountant"] },
  { to: "/reports", icon: BarChart3, label: "Reports", testid: "nav-reports", roles: ["admin", "accountant"] },
  { to: "/import", icon: Upload, label: "Import", testid: "nav-import", roles: ["admin"] },
  { to: "/audit", icon: Shield, label: "Audit Log", testid: "nav-audit", roles: ["admin", "accountant"] },
  { to: "/users", icon: UserCog, label: "Users", testid: "nav-users", roles: ["admin"] },
];

function SidebarInner({ nav, user, logout, closeDrawer }) {
  return (
    <>
      <div className="px-6 py-5 flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center shadow-lg shadow-blue-500/20">
          <Car size={20} className="text-white" strokeWidth={2.2} />
        </div>
        <div>
          <div className="font-display font-bold text-base leading-none tracking-tight">MotoCraft</div>
          <div className="text-[10px] text-muted-foreground tracking-[0.15em] uppercase mt-1">Dealer OS</div>
        </div>
      </div>

      <nav className="flex-1 px-3 py-2 overflow-y-auto" data-testid="sidebar-nav">
        {nav.map(({ to, icon: Icon, label, testid, end }) => (
          <NavLink
            key={to}
            to={to}
            end={end}
            data-testid={testid}
            onClick={closeDrawer}
            className={({ isActive }) =>
              `group flex items-center gap-3 px-3 py-2.5 rounded-xl text-sm font-medium mb-1 transition-all duration-200 ${
                isActive
                  ? "bg-gradient-to-r from-blue-500/10 to-blue-600/5 text-blue-700 dark:text-blue-300 shadow-sm"
                  : "text-slate-600 hover:bg-slate-100/60 hover:text-slate-900 dark:text-slate-400 dark:hover:bg-slate-800/60 dark:hover:text-slate-100"
              }`
            }
          >
            {({ isActive }) => (
              <>
                <span className={`w-1 h-5 rounded-full transition-all ${isActive ? "bg-blue-600" : "bg-transparent"}`} />
                <Icon size={17} strokeWidth={1.75} />
                <span>{label}</span>
              </>
            )}
          </NavLink>
        ))}
      </nav>

      <div className="px-3 pb-3">
        <div className="surface flex items-center gap-3 px-3 py-2.5">
          <div className="w-9 h-9 rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center text-white text-xs font-bold ring-2 ring-blue-500/20">
            {(user?.name || "U").charAt(0).toUpperCase()}
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold truncate" data-testid="user-name">{user?.name}</div>
            <div className="text-[10px] text-muted-foreground uppercase tracking-[0.12em]">{user?.role}</div>
          </div>
          <button data-testid="logout-button" onClick={logout} className="p-2 rounded-lg hover:bg-red-50 dark:hover:bg-red-500/10 text-slate-500 hover:text-red-600 transition-colors">
            <LogOut size={15} />
          </button>
        </div>
      </div>
    </>
  );
}

export default function Layout() {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const nav = NAV_ALL.filter((n) => n.roles.includes(user?.role));
  const closeDrawer = () => setDrawerOpen(false);

  return (
    <div className="min-h-screen text-foreground">
      {/* Desktop sidebar */}
      <aside className="hidden md:flex md:w-64 md:fixed md:inset-y-0 md:flex-col glass border-r border-border/60 z-30">
        <SidebarInner nav={nav} user={user} logout={logout} closeDrawer={closeDrawer} />
      </aside>

      {/* Mobile drawer */}
      {drawerOpen && (
        <div className="md:hidden fixed inset-0 z-50">
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={closeDrawer} />
          <aside className="absolute inset-y-0 left-0 w-72 glass border-r border-border/60 flex flex-col animate-fade-up">
            <div className="flex justify-end px-3 pt-3"><button onClick={closeDrawer} className="p-2 rounded-lg hover:bg-secondary"><X size={18} /></button></div>
            <SidebarInner nav={nav} user={user} logout={logout} closeDrawer={closeDrawer} />
          </aside>
        </div>
      )}

      <div className="md:ml-64 flex flex-col min-h-screen">
        {/* Header */}
        <header className="sticky top-0 z-20 glass border-b border-border/60 h-16 flex items-center px-4 md:px-8 gap-3">
          <button data-testid="open-drawer" onClick={() => setDrawerOpen(true)} className="md:hidden p-2 rounded-lg hover:bg-secondary">
            <Menu size={18} />
          </button>
          <div className="md:hidden font-display font-bold">MotoCraft</div>
          <div className="hidden md:flex relative flex-1 max-w-md">
            <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              data-testid="global-search"
              placeholder="Search VIN, stock, make, customer…"
              className="w-full pl-10 pr-3 py-2 rounded-xl bg-secondary/70 border border-transparent text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-blue-500/30 focus:border-blue-500/40 transition"
              onKeyDown={(e) => { if (e.key === "Enter" && e.target.value.trim()) { navigate(`/inventory?q=${encodeURIComponent(e.target.value.trim())}`); } }}
            />
          </div>
          <div className="flex-1 md:hidden" />
          <button data-testid="theme-toggle" onClick={toggle} aria-label="Toggle theme" className="p-2 rounded-lg hover:bg-secondary text-slate-600 dark:text-slate-300 transition-colors">
            {theme === "dark" ? <Sun size={18} /> : <Moon size={18} />}
          </button>
          <button aria-label="Notifications" className="p-2 rounded-lg hover:bg-secondary text-slate-600 dark:text-slate-300 transition-colors relative">
            <Bell size={18} />
            <span className="absolute top-1.5 right-1.5 w-1.5 h-1.5 rounded-full bg-blue-600" />
          </button>
        </header>

        <main className="flex-1 p-4 md:p-8 animate-fade-up">
          <Outlet />
        </main>

        <Toaster position="top-right" />
      </div>
    </div>
  );
}
